import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import api from 'services/api';
import { useValidation } from 'hooks';

import { Radio, Input, Text, Select, Badge } from 'v3/components';

import { useSnackbar } from 'v3/components/Snackbar';
import BranchOfficesSelect, { toBranchOfficeIds } from 'v3/components/BranchOfficesSelect';
import { Row, Col } from 'react-bootstrap';

import {
  fetchVehiclesTypes,
  fetchBodies,
  fetchAnttTypes,
  fetchOnlyCities,
  fetchImplements,
  fetchOwners,
  fetchTrackerTypes,
  fetchTrackers,
} from 'utils/fetches';

import { TravelContext } from '../../controller';

import { vehicleRegistrationSchema } from '../../validator';

const [, useTravel] = TravelContext;

const ManualRegisterVehicle = forwardRef(
  ({ data, setData, errors, setErrors, loading, setLoading, sendResponse, handleClose }, ref) => {
    const travel = useTravel();

    const [trackers, setTrackers] = useState([]);
    const [trackerTypes, setTrackerTypes] = useState(null);
    const [vehicleTypesOptions, setVehicleTypesOptions] = useState(null);
    const [vehicleBodyTypesOptions, setVehicleBodyTypesOptions] = useState(null);
    const [vehicleImplementTypesOptions, setVehicleImplementTypesOptions] = useState(null);
    const [anttTypesOptions, setAnttTypesOptions] = useState(null);
    const snackbar = useSnackbar();

    const [validationErrors, { validate }] = useValidation(vehicleRegistrationSchema);

    const shouldDisplayImplement = data?.vehicleType?.qty_plates > 1 ?? false;

    const handleChange = (field, value) => {
      setData((old) => ({
        ...old,
        [field]: value,
      }));
    };

    useEffect(() => {
      setErrors(validationErrors);
    }, [validationErrors]);

    useEffect(() => {
      fetchOptions();
    }, []);

    async function fetchOptions() {
      try {
        let [vehicleTypesData, vehiclesBodyTypesData, implementsData, trackersData, anttTypesData] =
          await Promise.all([
            fetchVehiclesTypes(),
            fetchBodies(),
            fetchImplements(),
            fetchTrackers(),
            fetchAnttTypes(),
          ]);
        vehicleTypesData = vehicleTypesData.filter(
          (vtype) => vtype.name !== 'Dolly' && vtype.name !== 'Semi Reboque',
        );
        setVehicleTypesOptions(vehicleTypesData);
        setVehicleBodyTypesOptions(vehiclesBodyTypesData);
        implementsData = implementsData.filter((vtype) => vtype.name !== 'Dolly');
        setVehicleImplementTypesOptions(implementsData);
        setTrackers(trackersData);
        setAnttTypesOptions(anttTypesData);
      } catch (error) {
        //
      } finally {
      }
    }

    function clearStates() {
      setData({});
      setTrackerTypes(null);
    }

    async function handleCreation() {
      const payload = {
        plate: data?.plate,
        owner_id: data?.ownerData?.id,
        antt: data?.antt,
        antt_adherence: data?.anttAdherence,
        antt_type: data?.anttType?.id,
        chassi: data?.chassi,
        renavam: data?.renavam,
        plate_city: data?.city?.name,
        plate_state: data?.city?.province?.uf,
        plate_city_id: data?.city?.id,
        tracked: data?.isTracked,
        tracker_id: data?.tracker?.id,
        tracker_type_id: data?.trackerType?.id,
        tracker_code: data?.trackerCode,
        year_manufacture: data?.yearManufacture,
        year_model: data?.yearModel,
        vehicle_type_id: data?.vehicleType?.id,
        vehicle_type_name: data?.vehicleType?.name,
        vehicle_body_type_id: data?.vehicleBodyType?.id,
        vehicle_implement_type: data?.vehicleImplementType?.id,
        beneficiary_id: data?.beneficiary?.id,
        branchOffices: data?.branchOffices,
      };
      const [isValid] = await validate(payload);
      if (!isValid) {
        snackbar.show(<Text>Erro ao cadastrar veículo</Text>, {
          type: 'error',
        });
        return false;
      }

      delete payload.vehicle_type_name;
      payload.branch_offices = toBranchOfficeIds(payload.branchOffices);
      delete payload.branchOffices;

      try {
        setLoading(true);
        const response = await api.post('vehicles', payload);
        snackbar.show(<Text>Cadastro realizado com sucesso</Text>, {
          type: 'success',
        });

        clearStates();

        if (typeof sendResponse === 'function') {
          sendResponse(response);
        }

        if (travel?.setData) {
          travel.setData((old) => ({
            ...old,
            vehicle: {
              ...old.vehicle,
              main: response.data?.vehicle,
            },
          }));
        }
        setLoading(false);

        handleClose();
      } catch (err) {
        snackbar.show(<Text>Erro ao cadastrar veículo</Text>, {
          type: 'error',
        });
        const errorsNew = err.response?.data;
        if (errorsNew && Array.isArray(errorsNew)) {
          setErrors((old) => ({
            ...old,
            ...Object.fromEntries(errorsNew.map((error) => [error.field, error.message])),
          }));
        }

        setLoading(false);
        return false;
      }
      return true;
    }

    useEffect(() => {
      async function fetchTopTrackerTypes(track) {
        try {
          const response = await api.get('select/tracker-types', {
            params: {
              tracker: track.id,
            },
          });
          setTrackerTypes(response.data);
        } catch (error) {
          // Ignore exception
        }
      }
      if (data?.tracker) {
        fetchTopTrackerTypes(data.tracker);
      }
    }, [data?.tracker]);

    useEffect(() => {
      if (!data?.isTracked) {
        setData((old) => ({
          ...old,
          trackerCode: '',
          tracker: null,
          trackerType: null,
        }));
      }
    }, [data?.isTracked]);

    useImperativeHandle(ref, () => ({
      save: handleCreation,
    }));

    return (
      <>
        <Row>
          <Col md={4} xs={12} className="mb-3">
            <Input
              label="Placa *"
              value={data?.plate || ''}
              onChange={(event) =>
                event.target.value.length > 7
                  ? snackbar.show(<Text> Limite de dígitos para Placa </Text>, { type: 'error' })
                  : handleChange('plate', event.target.value.toUpperCase())
              }
              error={errors?.plate}
              id="input-novo-veiculo-modal-placa"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Select.Async
              label="Cidade *"
              onSearch={fetchOnlyCities}
              value={data?.city}
              getOptionLabel={(option) => `${option?.name}, ${option?.province?.uf ?? option?.uf}`}
              getOptionValue={(option) => option.id}
              onChange={(value) => {
                handleChange('city', value);
              }}
              error={errors?.plate_city_id}
              id="select-novo-veiculo-modal-cidade"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Input
              label="Ano de fabricação *"
              type="number"
              value={data?.yearManufacture}
              onChange={(event) =>
                event.target.value.length > 4
                  ? snackbar.show(<Text> Limite de dígitos para Ano </Text>, {
                      type: 'error',
                    })
                  : handleChange('yearManufacture', event.target.value)
              }
              error={errors?.year_manufacture}
              id="input-novo-veiculo-modal-ano-fabricacao"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Input
              label="Ano modelo *"
              type="number"
              value={data?.yearModel}
              onChange={(event) =>
                event.target.value.length > 4
                  ? snackbar.show(<Text> Limite de dígitos para Ano </Text>, {
                      type: 'error',
                    })
                  : handleChange('yearModel', event.target.value)
              }
              error={errors?.year_model}
              id="input-novo-veiculo-modal-ano-modelo"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Input
              label="Chassi *"
              value={data?.chassi || ''}
              onChange={(event) =>
                event.target.value.length > 20
                  ? snackbar.show(<Text> Limite de dígitos para Chassi </Text>, {
                      type: 'error',
                    })
                  : handleChange('chassi', event.target.value)
              }
              error={errors?.chassi}
              id="input-novo-veiculo-modal-chassi"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Select
              label="Tipo do veículo *"
              options={vehicleTypesOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              value={data?.vehicleType}
              onChange={(value) => {
                handleChange('vehicleType', value);
                setErrors((old) => ({
                  ...old,
                  vehicle_implement_type: null,
                }));
              }}
              error={errors?.vehicle_type_id}
              id="select-novo-veiculo-modal-tipo-veiculo"
            />
          </Col>
          {shouldDisplayImplement && (
            <Col md={4} xs={12} className="mb-3">
              <Select
                label="Tipo de implemento *"
                options={vehicleImplementTypesOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={data?.vehicleImplementType}
                onChange={(value) => handleChange('vehicleImplementType', value)}
                error={errors?.vehicle_implement_type}
                id="select-novo-veiculo-modal-tipo-implemento"
              />
            </Col>
          )}
          <Col md={4} xs={12} className="mb-3">
            <Select
              label="Tipo do carroceria *"
              options={vehicleBodyTypesOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              value={data?.vehicleBodyType}
              onChange={(value) => handleChange('vehicleBodyType', value)}
              error={errors?.vehicle_body_type_id}
              id="select-novo-veiculo-modal-tipo-carroceria"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Input
              label="Renavam *"
              type="number"
              value={data?.renavam}
              onChange={(event) =>
                event.target.value.length > 11
                  ? snackbar.show(<Text> Limite de dígitos para Renavam </Text>, {
                      type: 'error',
                    })
                  : handleChange('renavam', event.target.value)
              }
              error={errors?.renavam}
              id="input-novo-veiculo-modal-renavam"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Select
              label="Tipo de RNTRC *"
              options={anttTypesOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              value={data?.anttType}
              onChange={(value) => handleChange('anttType', value)}
              error={errors?.antt_type}
              id="select-novo-veiculo-modal-tipo-rntrc"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Input
              label="RNTRC *"
              type="number"
              value={data?.antt}
              onChange={(event) => handleChange('antt', event.target.value)}
              error={errors?.antt}
              id="input-novo-veiculo-modal-rntrc"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Input
              label="Data de emissão da RNTRC *"
              type="date"
              value={data?.anttAdherence}
              onChange={(event) => handleChange('anttAdherence', event.target.value)}
              error={errors?.antt_adherence}
              id="input-novo-veiculo-modal-data-emissao-rntrc"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Select.Async
              label="Proprietário do documento *"
              onSearch={fetchOwners}
              onChange={(value) => handleChange('ownerData', value)}
              value={data?.ownerData}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.id}
              error={errors?.owner_id}
              id="select-novo-veiculo-modal-proprietario-documento"
            />
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Select.Async
              label="Beneficiário do veículo *"
              onSearch={fetchOwners}
              onChange={(value) => handleChange('beneficiary', value)}
              value={data?.beneficiary}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.id}
              error={errors?.beneficiary_id}
            />
          </Col>

          <Col md={4} xs={12} className="mb-3">
            <Radio.Group
              label="Rastreado *"
              onChange={({ target }) => handleChange('isTracked', target.value)}
              value={data?.isTracked}
              horizontal
              error={errors?.isTracked}
            >
              <Radio id="radio-novo-veiculo-modal-rastreado-sim" value>
                <Text color="dark" type="label">
                  Sim
                </Text>
              </Radio>
              <Radio id="radio-novo-veiculo-modal-rastreado-nao" value={false}>
                <Text color="dark" type="label">
                  Não
                </Text>
              </Radio>
            </Radio.Group>
            {errors?.tracked && (
              <Badge className="mt-1" light variant="error">
                Campo obrigatório
              </Badge>
            )}
          </Col>
          <Col md={4} xs={12} className="mb-3">
            <Select
              placeholder={data?.isTracked ? 'Selecione...' : 'Veículo não rastreado'}
              isDisabled={!data?.isTracked}
              label={data?.isTracked ? 'Marca rastreador *' : 'Marca rastreador'}
              options={trackers}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              value={data?.isTracked ? data?.tracker : null}
              onChange={(value) => handleChange('tracker', value)}
              error={errors?.tracker_id}
              id="select-novo-veiculo-modal-marca-rastreador"
            />
          </Col>

          <Col md={4} xs={12} className="mb-3">
            <Select.Async
              onSearch={(search) => fetchTrackerTypes(search, data?.tracker)}
              value={data?.tracker ? data?.trackerType : null}
              onChange={(value) => handleChange('trackerType', value)}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              label="Tipo de rastreador"
              placeholder={
                data?.isTracked
                  ? data?.tracker
                    ? 'Selecione...'
                    : 'Veículo sem rastreador'
                  : 'Veículo não rastreado'
              }
              options={trackerTypes}
              isDisabled={!data?.isTracked || !data?.tracker}
              error={errors?.tracker_type_id}
              NoOptionsComponent={() => (
                <Text>Nenhum tipo cadastrado para o rastreador informado</Text>
              )}
              id="select-novo-veiculo-modal-tipo-rastreador"
            />
          </Col>

          <Col md={4} xs={12} className="mb-3">
            <Input
              placeholder={data?.isTracked ? 'Digite...' : 'Veículo não rastreado'}
              disabled={!data?.isTracked}
              label={'ID do rastreador'}
              value={data?.trackerCode}
              onChange={(event) => handleChange('trackerCode', event.target.value)}
              error={errors?.tracker_code}
              id="input-novo-veiculo-modal-id-rastreador"
            />
          </Col>

          <Col xs={12} className="mb-3">
            <BranchOfficesSelect
              value={data?.branchOffices}
              onChange={(value) => handleChange('branchOffices', value)}
              error={errors?.branchOffices}
            />
          </Col>
        </Row>
      </>
    );
  },
);

export default ManualRegisterVehicle;
